import { motion } from 'framer-motion';

/**
 * Branded Suspense fallback shown while a lazy route chunk downloads.
 *
 * Contract:
 * - Never a blank white screen — always inherits `#0A1514` background.
 * - Accessible: `role="status"` + `aria-live="polite"` + `aria-label`.
 * - Layout-stable: fills the viewport at min-h-[100dvh] so lazy routes
 *   don't cause header/footer jump when they resolve.
 * - Works on native Capacitor: no window-only APIs, no eager network.
 * - Reduced-motion friendly — the ring keeps a static outline when the
 *   user prefers reduced motion (via framer-motion's built-in respect).
 */
export default function RouteSuspenseFallback({ label = 'Loading…' }) {
  return (
    <div
      data-testid="route-suspense-fallback"
      role="status"
      aria-live="polite"
      aria-label={label}
      className="fixed inset-0 z-[9999] min-h-[100dvh] w-full flex items-center justify-center bg-[#0A1514] text-white"
      style={{ fontFamily: "'Manrope', sans-serif" }}
    >
      <div className="flex flex-col items-center gap-4">
        {/* Small enso-inspired ring — a static SVG that matches the brand
            palette without pulling in the full mark PNG (keeps the
            fallback itself tiny). */}
        <motion.svg
          viewBox="0 0 60 60"
          className="w-9 h-9"
          initial={{ opacity: 0.4 }}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          aria-hidden="true"
        >
          <circle
            cx="30"
            cy="30"
            r="24"
            fill="none"
            stroke="#5EEAD4"
            strokeWidth="3.5"
            strokeLinecap="round"
            strokeDasharray="130 30"
            transform="rotate(-90 30 30)"
          />
        </motion.svg>
        <span className="text-xs tracking-[0.28em] uppercase text-[#94A3B8]">
          {label}
        </span>
      </div>
    </div>
  );
}
